import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import type { NavigationItemProps } from '@app/src/components/Navigation/VerticalMenu/NavigationItems/NavigationItems'
import { AssessmentViews } from '@app/src/pages/Assessments/Assessment/assessmentViews'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import navigationLinks from '@app/src/wf-constants/navigationLinks'
import paths from '@app/src/wf-constants/paths'
import { CircularProgress, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router-dom'
import { navLabelSx, NAV_ICON_SIZE, NAV_ITEM_HEIGHT, NAV_ITEM_PX } from '../navItemStyles'
import { useVerticalMenu } from '../VerticalMenuContext'
import type { MenuWrapperProps } from './MenuWrapper'
import MenuWrapper from './MenuWrapper'

const AssessmentMenu: React.FC<NavigationItemProps> = ({ icon }) => {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const { assessmentSettings, isLoading: isAssessmentSettingsLoading } = useFetchAssessmentSettings()
  const { isExpanded } = useVerticalMenu()

  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)
  const enabledAssessmentTemplates = assessmentSettings?.filter(setting => setting.isActivated) ?? []
  const enabledAssessmentTemplateIds = enabledAssessmentTemplates.map(setting => setting.assessmentTemplate.id)
  const hasAssessmentsEnabled = Boolean(enabledAssessmentTemplates.length)

  const {
    facets: [assessmentProviderFacets = []],
    isFetched: isAssessmentProviderFacetsFetched,
    isLoading: isAssessmentProviderFacetsLoading,
  } = useFetchFacets({
    key: [FetchKey.AssessmentFacets, 'navbar-providers'],
    endpoint: endpoints.assessmentFacet,
    facetsParam: [{ name: 'assessmentTemplateId', isEnum: true }],
    filter: [
      {
        name: 'assessmentTemplateId',
        filters: [{ value: enabledAssessmentTemplateIds, operator: Operators.In }],
      },
    ],
    options: { enabled: hasAssessmentsEnabled, staleTime: ONE_MINUTE_MS },
  })

  const assessmentTemplatesWithData = useMemo(() => {
    if (!isAssessmentProviderFacetsFetched) return []

    const assessmentTemplateIdsWithData = assessmentProviderFacets.map(facet => Number(facet.value))

    return enabledAssessmentTemplates.filter(setting =>
      assessmentTemplateIdsWithData.includes(setting.assessmentTemplate.id),
    )
  }, [enabledAssessmentTemplates, assessmentProviderFacets, isAssessmentProviderFacetsFetched])

  const sortedAssessmentTemplates = useMemo(() => {
    const baseline = assessmentTemplatesWithData.filter(
      s => s.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment,
    )
    const others = assessmentTemplatesWithData
      .filter(s => s.assessmentTemplate.assessmentType !== AssessmentType.BaselineAssessment)
      .sort((a, b) => a.assessmentTemplate.name.localeCompare(b.assessmentTemplate.name))

    return [...baseline, ...others]
  }, [assessmentTemplatesWithData])

  if (!canAccessAssessmentTemplate || !hasAssessmentsEnabled) return null

  const isLoading = isAssessmentSettingsLoading || isAssessmentProviderFacetsLoading

  if (isLoading) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        sx={{ minHeight: NAV_ITEM_HEIGHT }}
        px={NAV_ITEM_PX}
        spacing={1}
      >
        <Stack width={NAV_ICON_SIZE} alignItems="center" flexShrink={0}>
          <CircularProgress size={16} />
        </Stack>
        <Typography variant="subtitle2" color="text.secondary" noWrap sx={navLabelSx(isExpanded)}>
          {formatMessage({ id: 'navbar.assessments' })}
        </Typography>
      </Stack>
    )
  }

  const menuItems: MenuWrapperProps['menuItems'] = [
    ...sortedAssessmentTemplates.map(setting => ({
      title: setting.assessmentTemplate.name,
      link: generatePath(paths.assessments, {
        view: AssessmentViews.Overview,
        assessmentTemplateId: setting.assessmentTemplate.id,
      }),
      permission: Permissions.ASSESSMENT_TEMPLATE_ACCESS,
    })),
    {
      title: formatMessage({ id: 'navbar.allAssessments' }),
      link: navigationLinks.allAssessments,
      permission: Permissions.ASSESSMENT_TEMPLATE_ACCESS,
    },
  ]

  return <MenuWrapper icon={icon} menuItems={menuItems} title={formatMessage({ id: 'navbar.assessments' })} />
}

export default AssessmentMenu
